const STICKY_MENU_CLOSE_DELAY_MS = 5000;
const STICKY_MENU_NAMES = new Set(['products', 'industries']);

export function getMenuCloseDelay(menuName) {
  return STICKY_MENU_NAMES.has(menuName) ? STICKY_MENU_CLOSE_DELAY_MS : 0;
}

export function nextMenuState(currentMenu, requestedMenu) {
  return currentMenu === requestedMenu ? null : requestedMenu;
}

export function getActiveProductMenuKey(requestedKey, availableKeys) {
  if (!Array.isArray(availableKeys) || availableKeys.length === 0) return null;

  return availableKeys.includes(requestedKey) ? requestedKey : availableKeys[0];
}

export function applyProductMenuState(categories, groups, requestedKey) {
  const availableKeys = categories.map((category) => category.dataset.productMenuKey);
  const activeKey = getActiveProductMenuKey(requestedKey, availableKeys);

  categories.forEach((category) => {
    const isActive = category.dataset.productMenuKey === activeKey;
    category.dataset.active = String(isActive);
    category.setAttribute?.('aria-expanded', String(isActive));
  });
  groups.forEach((group) => {
    group.hidden = group.dataset.productMenuGroup !== activeKey;
  });

  return activeKey;
}

export function getExpandedMenuToggle(menus) {
  return menus.find(({ toggle }) => toggle.getAttribute('aria-expanded') === 'true')?.toggle || null;
}

export function getProductMenuFocusTarget(direction, category, group) {
  if (direction === 'products') return group?.querySelector('a') || null;
  if (direction === 'category') return category || null;

  return null;
}

export function shouldPreviewProductCategory(pointerType, requestedKey, activeKey) {
  return pointerType === 'touch' && requestedKey !== activeKey;
}

export function initLinendipityHeader(root) {
  if (!root) return () => {};

  const toggles = [...root.querySelectorAll('[data-menu-toggle]')];
  const menus = toggles.map((toggle) => {
    const name = toggle.dataset.menuToggle;
    const wrapper = toggle.closest('[data-menu]');
    const panel = root.querySelector(`[data-menu-panel="${name}"]`);

    return { name, toggle, wrapper, panel };
  });
  const productCategories = [...root.querySelectorAll('[data-product-menu-key]')];
  const productGroups = [...root.querySelectorAll('[data-product-menu-group]')];

  let openMenu = null;
  let closeTimer = null;
  let activeProductMenuKey = applyProductMenuState(productCategories, productGroups, '');
  let previewTouchedProductCategory = false;

  const clearCloseTimer = () => {
    if (closeTimer === null) return;

    clearTimeout(closeTimer);
    closeTimer = null;
  };

  const setMenu = (requestedMenu) => {
    clearCloseTimer();
    openMenu = requestedMenu === null ? null : nextMenuState(openMenu, requestedMenu);

    if (openMenu === 'products') {
      activeProductMenuKey = applyProductMenuState(productCategories, productGroups, '');
    }

    menus.forEach(({ name, toggle, panel }) => {
      const isOpen = name === openMenu;

      toggle.setAttribute('aria-expanded', String(isOpen));
      if (panel) panel.hidden = !isOpen;
    });

    root.dataset.openMenu = openMenu || '';
    root.classList.toggle('ld-menu-open', Boolean(openMenu));
  };

  const scheduleMenuClose = (name) => {
    clearCloseTimer();

    const delay = getMenuCloseDelay(name);
    if (delay === 0) {
      setMenu(null);
      return;
    }

    closeTimer = setTimeout(() => {
      closeTimer = null;
      if (openMenu === name) setMenu(null);
    }, delay);
  };

  const listeners = [];
  const listen = (element, eventName, handler) => {
    element?.addEventListener(eventName, handler);
    if (element) listeners.push(() => element.removeEventListener(eventName, handler));
  };

  const activateProductGroup = (category) => {
    activeProductMenuKey = applyProductMenuState(
      productCategories,
      productGroups,
      category.dataset.productMenuKey,
    );
  };

  productCategories.forEach((category) => {
    listen(category, 'pointerenter', (event) => {
      if (event.pointerType !== 'touch') activateProductGroup(category);
    });
    listen(category, 'focus', () => activateProductGroup(category));
    listen(category, 'pointerdown', (event) => {
      previewTouchedProductCategory = shouldPreviewProductCategory(
        event.pointerType,
        category.dataset.productMenuKey,
        activeProductMenuKey,
      );
    });
    listen(category, 'click', (event) => {
      const shouldPreview = previewTouchedProductCategory;

      previewTouchedProductCategory = false;
      if (!shouldPreview) return;

      event.preventDefault();
      activateProductGroup(category);
    });
    listen(category, 'keydown', (event) => {
      if (event.key !== 'ArrowRight') return;

      const activeGroup = productGroups.find(
        (group) => group.dataset.productMenuGroup === category.dataset.productMenuKey,
      );
      const focusTarget = getProductMenuFocusTarget('products', category, activeGroup);
      if (!focusTarget) return;

      event.preventDefault();
      focusTarget.focus();
    });
  });

  productGroups.forEach((group) => {
    listen(group, 'keydown', (event) => {
      if (event.key !== 'ArrowLeft') return;

      const activeCategory = productCategories.find(
        (category) => category.dataset.productMenuKey === group.dataset.productMenuGroup,
      );
      const focusTarget = getProductMenuFocusTarget('category', activeCategory, group);
      if (!focusTarget) return;

      event.preventDefault();
      focusTarget.focus();
    });
  });

  menus.forEach((menu) => {
    const { toggle, wrapper, name } = menu;
    const openOnPointerEnter = (event) => {
      if (event.pointerType !== 'touch') {
        clearCloseTimer();
        if (openMenu !== name) setMenu(name);
      }
    };
    const openOnFocus = () => {
      clearCloseTimer();
      if (openMenu !== name) setMenu(name);
    };
    const toggleOnClick = (event) => {
      event.preventDefault();
      setMenu(name);
    };
    const closeOnPointerLeave = () => {
      if (!wrapper?.matches(':focus-within')) scheduleMenuClose(name);
    };
    const closeOnFocusOut = (event) => {
      if (!wrapper?.contains(event.relatedTarget)) setMenu(null);
    };

    listen(wrapper, 'pointerenter', openOnPointerEnter);
    listen(toggle, 'focus', openOnFocus);
    listen(toggle, 'click', toggleOnClick);
    listen(wrapper, 'pointerleave', closeOnPointerLeave);
    listen(wrapper, 'focusout', closeOnFocusOut);
  });

  const search = root.querySelector('.ld-predictive-search');
  const closeMenuForSearch = () => setMenu(null);

  listen(search, 'pointerdown', closeMenuForSearch);
  listen(search, 'focusin', closeMenuForSearch);

  const closeOnOutsideClick = (event) => {
    if (!root.contains(event.target)) setMenu(null);
  };
  const closeOnEscape = (event) => {
    if (event.key === 'Escape') {
      const expandedToggle = getExpandedMenuToggle(menus);
      setMenu(null);
      expandedToggle?.focus();
    }
  };

  listen(document, 'click', closeOnOutsideClick);
  listen(document, 'keydown', closeOnEscape);
  setMenu(null);

  return () => {
    clearCloseTimer();
    listeners.forEach((removeListener) => removeListener());
  };
}

if (typeof document !== 'undefined') {
  const mountHeaders = () => {
    document.querySelectorAll('[data-linendipity-header]').forEach((root) => {
      if (root.dataset.headerInitialized === 'true') return;

      initLinendipityHeader(root);
      root.dataset.headerInitialized = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountHeaders, { once: true });
  } else {
    mountHeaders();
  }
}
